import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  linkedSignal,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, RouterLink } from '@angular/router';
import {
  CalendarItem,
  CalendarItemGroup,
  formatMinuteRange,
  minutesToPixels,
} from '@techspert-io/schedule-calendar';
import { CallSchedulePeriod } from '../../models/interview-period.models';

type CalendarCall = CalendarItem<CallSchedulePeriod>;

/** Room the shown call's name and time need, one above the other */
const MIN_TIME_HEIGHT = 32;
/** Room the shown call's name needs, with the padding above it */
const MIN_NAME_HEIGHT = 16;
/** Below this height the stepper sits beside the name rather than below it */
const MIN_STEPPER_ROW_HEIGHT = 34;
/** The stepper's height, with its margin from the bottom of the group */
const STEPPER_HEIGHT = 18;
/** The stepper's margin from the top of the group, when beside the name */
const COMPACT_STEPPER_TOP = 3;

/**
 * Calls that overlap, shown one at a time with a stepper to move between them.
 * The shown call is highlighted against the dimmed span of the whole group.
 */
@Component({
  selector: 'app-interview-period-calendar-call',
  templateUrl: './interview-period-calendar-call.component.html',
  styleUrls: ['./interview-period-calendar-call.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, MatIconModule, MatTooltipModule],
  host: {
    '[style.top.px]': 'top()',
    '[style.height.px]': 'height()',
  },
})
export class InterviewPeriodCalendarCallComponent {
  readonly group = input.required<CalendarItemGroup<CalendarCall>>();

  private readonly route = inject(ActivatedRoute);

  readonly publicDisplayId = this.route.snapshot.paramMap.get('id');

  /** The shown call, kept while it's still in the group */
  readonly index = linkedSignal<CalendarItemGroup<CalendarCall>, number>({
    source: this.group,
    computation: ({ items }, previous) => {
      const shownCallId =
        previous?.source.items[previous.value]?.item.callId ?? null;

      return Math.max(
        items.findIndex(({ item }) => item.callId === shownCallId),
        0
      );
    },
  });

  readonly current = computed(() => this.group().items[this.index()]);

  readonly top = computed(() => minutesToPixels(this.group().start) + 1);
  readonly height = computed(
    () => minutesToPixels(this.group().end - this.group().start) - 2
  );

  /** The shown call's offset within the group */
  readonly focusTop = computed(() =>
    minutesToPixels(this.current().start - this.group().start)
  );

  /** Kept within the group, which is trimmed to leave a gap below it */
  readonly focusHeight = computed(
    () =>
      Math.min(
        minutesToPixels(this.current().end - this.group().start),
        this.height()
      ) - this.focusTop()
  );

  readonly time = computed(() => formatMinuteRange(this.current()));

  readonly tooltip = computed(() =>
    this.group()
      .items.map((call) =>
        [call.item.expertName, formatMinuteRange(call)].join(' · ')
      )
      .join('\n')
  );

  readonly hasMultiple = computed(() => this.group().items.length > 1);

  readonly count = computed(
    () => `${this.index() + 1}/${this.group().items.length}`
  );

  /** The stepper sits beside the name when there's no room below it */
  readonly isCompact = computed(
    () => this.hasMultiple() && this.height() < MIN_STEPPER_ROW_HEIGHT
  );

  /** Room for the shown call's details, above the stepper when it reaches it */
  private readonly detailsHeight = computed(() => {
    if (!this.hasMultiple() || this.isCompact()) {
      return this.focusHeight();
    }

    const stepperTop = this.height() - STEPPER_HEIGHT;

    return (
      Math.min(this.focusTop() + this.focusHeight(), stepperTop) -
      this.focusTop()
    );
  });

  /** The name and time are left to the tooltip when there's no room for them */
  readonly showName = computed(
    () => !this.hasMultiple() || this.detailsHeight() >= MIN_NAME_HEIGHT
  );

  readonly showTime = computed(
    () => !this.hasMultiple() || this.detailsHeight() >= MIN_TIME_HEIGHT
  );

  /** The stepper flips to dark when it's over the dimmed rest of the group */
  readonly isStepperOnCall = computed(() => {
    const stepperMiddle = this.isCompact()
      ? COMPACT_STEPPER_TOP + STEPPER_HEIGHT / 2
      : this.height() - STEPPER_HEIGHT / 2;

    return (
      stepperMiddle >= this.focusTop() &&
      stepperMiddle <= this.focusTop() + this.focusHeight()
    );
  });

  previous() {
    this.step(-1);
  }

  next() {
    this.step(1);
  }

  private step(by: number) {
    const total = this.group().items.length;

    this.index.update((index) => (index + by + total) % total);
  }
}
